import { action, makeObservable, observable, runInAction } from 'mobx';
import RootStore from './RootStore';
import packagejson from '../../package.json';
import io, { Socket } from 'socket.io-client';
import { utcMoment } from '../utils/date';
import {
  IOnCompleteInquiry,
  IOnCreateInquiry,
  IOnCreateReply,
  IOnReadInquiry,
  IOnUpdateTypingState,
  SocketEventEnum,
  SocketEventKeyEnum,
} from '../interfaces/socket-event.interface';
import { MODE_SOCKET_KEEP_ACTIVE } from '../defines/mode';

export default class SocketStore {
  private readonly rootStore: RootStore;

  @observable messageSocket: Socket | undefined = undefined;

  @observable state = {
    isSocketConnected: true,
  };

  @action
  initializeSocket = () => {
    const { user, token } = this.rootStore.userStore;
    if (user.userId) {
      console.log('initializeSocket', user.userId);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      window.io = io;

      const socketServerDestination = process.env.REACT_APP_SOCKET_DEST
        ? `${process.env.REACT_APP_SOCKET_DEST}/message`
        : '/message';

      console.log('socket connection to', socketServerDestination);

      this.messageSocket = io(socketServerDestination, {
        // yield 'disconnect' event when window is closed
        // https://socket.io/blog/socket-io-4-1-0/
        closeOnBeforeunload: false,
        query: {
          version: packagejson.version,
          userId: user.userId,
          token,
        },
        transports: ['polling'],
      });

      this.messageSocket.on(
        SocketEventKeyEnum.createInquiry,
        this.onCreateMessage,
      );

      this.messageSocket.on(
        SocketEventKeyEnum.createReply,
        this.onCreateMessage,
      );

      this.messageSocket.on(SocketEventKeyEnum.readInquiry, this.onReadInquiry);

      this.messageSocket.on(
        SocketEventKeyEnum.completeInquiry,
        this.onCompleteInquiry,
      );

      this.messageSocket.on(SocketEventEnum.connect, () => {
        this.state.isSocketConnected = true;
      });

      this.messageSocket.on(SocketEventEnum.connectError, () => {
        this.state.isSocketConnected = false;
      });

      this.messageSocket.on(SocketEventEnum.disconnect, () => {
        this.state.isSocketConnected = false;
      });

      this.messageSocket.on(
        SocketEventKeyEnum.updateTypingState,
        this.onUpdateTypingState,
      );

      this.messageSocket.on('disconnect', () => {
        this.emitWatchingState('blur');
      });

      this.messageSocket.on('close', () => {
        this.emitWatchingState('blur');
      });
    }
  };

  @action
  disconnect = () => {
    if (this.messageSocket) {
      this.messageSocket.close();
    }
  };

  @action
  onCreateMessage = async (event: IOnCreateInquiry | IOnCreateReply) => {
    console.log('onCreateMessage', event);
    const { inquiryStore, messageStore, userStore } = this.rootStore;
    const { activeInquiryId } = inquiryStore;
    const { user } = userStore;

    if (event.key === SocketEventKeyEnum.createInquiry) {
      console.log(); // FIXME remove if unnecessary
    }

    const utcMessageSentTimestamp = utcMoment(
      event.message.messageSentTimestamp,
    ).format('YYYY-MM-DDTHH:mm:ss.SSSZZ');

    const messageSender =
      event.key === SocketEventKeyEnum.createInquiry
        ? (event as IOnCreateInquiry).senderInquiry.userId
        : (event as IOnCreateReply).senderInquiry.where.userId;

    if (user.userId === messageSender) {
      await inquiryStore.onMessageSent(
        event.message.inquiryId,
        event.message.messageBody,
        utcMessageSentTimestamp,
      );
    } else {
      await inquiryStore.onMessageReceived(
        event.message.inquiryId,
        event.message.messageBody,
        utcMessageSentTimestamp,
      );
      // this.rootStore.notificationStore.notification(event); // FIXME remove if unnecessary
    }
    if (activeInquiryId === event.message.inquiryId) {
      runInAction(() => {
        messageStore.messages.set(event.message.messageSentTimestamp, {
          ...event.message,
          messageReadTimestamp: null,
          messageSentTimestamp: utcMessageSentTimestamp,
        });
      });
      setTimeout(() => {
        this.rootStore.inquiryPageStore.lastMessageDivScrollIntoView('smooth');
      }, 10);
      await messageStore.updateMessageReadStatus();
    }
  };

  @action
  onReadInquiry = async (event: IOnReadInquiry) => {
    // console.log('onReadInquiry', event);
    // const { activeInquiryId } = this.rootStore.inquiryStore;
    // const { user } = this.rootStore.userStore;
    // if (event.inquiry.where.inquiryId === activeInquiryId) {
    //   this.rootStore.messageStore.readMessagesByRecipients(
    //     event.inquiry.values.lastUpdateTimestamp!,
    //   );
    // }
    // if (event.inquiry.where.userId === user.userId) {
    //   this.rootStore.inquiryStore.readInquiry(event.inquiry.where.userId);
    // }
  };

  @action
  onCompleteInquiry = async (event: IOnCompleteInquiry) => {
    // if (this.rootStore.serviceStore.showCompleteButton) {
    //   console.log('onCompleteInquiry', event);
    //   for (const inquiry of event.inquiries) {
    //     await this.rootStore.inquiryStore.setCompleteStatus(
    //       inquiry.where.inquiryId,
    //       inquiry.values.completeStatus!,
    //       false,
    //     );
    //   }
    //   this.rootStore.inquiryPageStore.setIsChangingCompleteStatus(false);
    // }
  };

  @action
  onUpdateTypingState = async (event: IOnUpdateTypingState) => {
    this.rootStore.inquiryStore.updateRecipientSideState(event);
  };

  @action
  emitWatchingState = (eventType: 'focus' | 'blur') => {
    const searchParams = new URLSearchParams(window.location.search);

    if (
      eventType === 'blur' &&
      searchParams.get('mode') === MODE_SOCKET_KEEP_ACTIVE
    ) {
      return;
    }

    this.messageSocket?.emit(SocketEventKeyEnum.updateWatchingState, {
      listeningEventType: eventType,
      emitUserId: this.rootStore.userStore.user.userId,
      inquiryId: this.rootStore.inquiryStore.activeInquiryId,
    });
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);
  }
}
